import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCardVariants } from '@backstage/core-components';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { WorkloadClient } from '@internal/workload-api-helper';
import { Card, CardHeader, Divider } from '@material-ui/core';
import CardContent from '@mui/material/CardContent';

import { useAsync } from 'react-use';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import { Features } from '../types';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { KMSKeyTableCard } from './KMSKeyTableCard';
import { useWorkload } from '../hooks/useWorkload';
import { useStyles } from '../styles';
import { StatusIndicatorTitle } from '../StatusIndicatorTitle';
import { KmsCardEditIcon } from './KmsCardEditIcon';

let accountObject: { [key: string]: string | undefined };

export type kms = {
  attribute_key_enabled?: boolean;
  attribute_key_share_accounts?: [];
  external_key_enabled?: boolean;
  external_key_share_accounts?: [];
  standard_key_share_accounts?: [];
};

export const FoundationManagedKMSCard = (props: {
  variant?: InfoCardVariants;
  title?: string;
}) => {
  const { title = 'Foundation Managed KMS' } = props;
  const { variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();

  const { workload: workloadData } = useWorkload();
  const identity = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { value: profile } = useAsync(() => identity.getProfileInfo());
  const workloadClient = new WorkloadClient(configApi, oauthApi);

  const features = entity?.spec?.features as Features;

  const parseWorkloadObjectValues = (
    catalogLocationStringList: string[] | undefined,
  ): { [key: string]: string | undefined } => {
    const resourceFileName: string | undefined =
      catalogLocationStringList?.pop();
    const resourceName = resourceFileName?.split('.')[0];
    const workload = catalogLocationStringList?.pop();

    return {
      resourceName,
      workload,
    };
  };

  const patchResourceData = async ({
    workload,
    resourceName,
    payload,
  }: {
    workload?: string;
    resourceName?: string;
    payload: { [_: string]: Object };
  }) => {
    return workloadClient.apiFetch(
      `workloads/workload/${workload}/resource/${resourceName}`,
      'PATCH',
      payload,
    );
  };

  const killProcessAndReportError = (e: any) => {
    errorApi.post(e);
  };

  const callPatchApi = async (updated_kms: {}) => {
    const payload: { [key: string]: Object } = updated_kms;
    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve(false);
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await patchResourceData({
          workload: accountObject.workload,
          resourceName: accountObject.resourceName,
          payload,
        });

        if (!isOkayResponse(patchResourceDataResponse)) {
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        } else {
          alertApi.post({
            message: getMessage({
              successMessage:
                'Updating Foundation Managed KMS configuration. Refresh this page to see the changes.',
            }).SUCCESS,
            severity: SeverityEnum.INFO,
          });

          await catalogApi.refreshEntity(stringifyEntityRef(entity));
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }
    return Promise.resolve(true);
  };

  const { value } = useAsync(async () => {
    const entities = await catalogApi.getEntities({
      filter: [{ kind: ['Resource'] }],
    });
    const accountName = entities.items
      .map(
        (element: Entity) =>
          `${element.metadata.annotations?.['amazonaws.com/account-id']} : ${element.metadata.name} `,
      )
      .filter(
        el => !el.startsWith('no-entry') && el !== null && el !== undefined,
      );
    return accountName;
  });

  const filterData: [] = value as [];

  const workloadFeature = {
    name: 'Learn more about Foundation Managed KMS keys',
    docs: '/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/foundation-managed-kms-keys',
  };

  const isEngineer = (): boolean => {
    return (
      workloadData?.engineerMembers
        ?.filter(member => member !== null)
        ?.map(member => member.toLowerCase())
        .includes(profile?.email?.toLowerCase() ?? '') ?? false
    );
  };

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  return (
    <Card className={cardClass}>
      <CardHeader
        title={title}
        action={
          <KmsCardEditIcon
            workloadData={workloadData}
            requesterEmail={profile?.email || ''}
            isEngineer={isEngineer}
            workloadFeature={workloadFeature}
            accountName={filterData}
            callPatchApi={callPatchApi}
            _kms={features?.kms}
          />
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <StatusIndicatorTitle enabled title="Standard Key" />
        <KMSKeyTableCard
          accounts={features?.kms?.standard_key_share_accounts}
        />
        <Divider style={{ margin: '16px 0' }} />
        <StatusIndicatorTitle
          enabled={features?.kms?.attribute_key_enabled}
          title="Attribute Key"
        />
        <KMSKeyTableCard
          accounts={features?.kms?.attribute_key_share_accounts}
        />
      </CardContent>
    </Card>
  );
};
